import {
  Token,
  Fetcher,
  Trade,
  Route,
  TokenAmount,
  TradeType,
} from '@uniswap/sdk';
import { ethers, Contract } from 'ethers';
import { timelockEncrypt } from 'ts-ibe';
import { TokenType, TypeOfSwap } from '../../types';
import { FAIRYRING_PUBKEY, pairAddress, routerAddress } from '../../constants';
import ERC20 from '../abis/ERC20.json';
import ROUTER from '../abis/NewUniswapV2Router.json';
import PAIR from '../abis/NewUniswapV2Pair.json';
import {
  countLeadingZeros,
  firstNonZeroValue,
  stringToUint8Array,
} from './string';
import { PUBLIC_ENVIRONMENT } from '../../config/env';

export const fetchPrice = async ({
  from,
  to,
  amount,
  chainId,
}: {
  from: TokenType;
  to: TokenType;
  amount: number;
  chainId: number;
}) => {
  const fromToken = new Token(chainId, from.address, from.decimals);
  const toToken = new Token(chainId, to.address, to.decimals);

  // note that you may want/need to handle this async code differently,
  // for example if top-level await is not an option
  const pair = await Fetcher.fetchPairData(fromToken, toToken);

  const route = new Route([pair], fromToken);

  const trade = new Trade(
    route,
    new TokenAmount(
      fromToken,
      ethers.utils.parseEther(amount.toString()).toString(),
    ),
    TradeType.EXACT_INPUT,
  );

  return trade.executionPrice.toSignificant(6);
};

export function getRouterContract(chainId: number, signer: ethers.Signer) {
  const routerContractAddress = routerAddress.get(chainId);
  return new Contract(routerContractAddress, ROUTER.abi, signer);
}

export function getPairContract(chainId: number, signer: ethers.Signer) {
  const pairContractAddress = pairAddress.get(chainId);
  return new Contract(pairContractAddress, PAIR.abi, signer);
}

export const swapTokens = async (
  from: TokenType,
  to: TokenType,
  amount: number | string,
  routerContract: Contract,
  accountAddress: string,
  signer: ethers.Signer,
  outputLimitPrice: number,
  isEncrypt = false,
  ratioOfLiquidities: number,
) => {
  const tokens = [from.address, to.address];
  const time = Math.floor(Date.now() / 1000) + 200000;
  const deadline = ethers.BigNumber.from(time);

  const token1 = new Contract(from.address, ERC20.abi, signer);
  const tokenDecimals = from.decimals;

  const amountIn = ethers.utils.parseUnits(
    typeof amount === 'number' ? amount.toString() : amount,
    tokenDecimals,
  );

  let amountOuts;
  let amountOut;
  if (!isEncrypt) {
    amountOuts = await routerContract.callStatic.getAmountsOut(
      amountIn,
      tokens,
    );
    amountOut = amountOuts[1];
  }

  const approveTx = await token1.approve(routerContract.address, amountIn);
  await approveTx.wait();
  const wethAddress = ''; // await routerContract.WETH();

  if (from.address === wethAddress) {
    // Eth -> Token
    await routerContract.swapExactETHForTokens(
      amountOut,
      tokens,
      accountAddress,
      deadline,
      { value: amountIn },
    );
  } else if (to.address === wethAddress) {
    // Token -> Eth
    await routerContract.swapExactTokensForETH(
      amountIn,
      amountOut,
      tokens,
      accountAddress,
      deadline,
    );
  } else {
    if (isEncrypt) {
      let price = '';
      let scale = 0;
      if (outputLimitPrice >= 10) {
        price = Math.ceil(ratioOfLiquidities * outputLimitPrice).toString();
        scale = 1;
      } else if (outputLimitPrice >= 1) {
        price = Math.floor(
          ratioOfLiquidities * outputLimitPrice * 10,
        ).toString();
        scale = 10;
      } else if (outputLimitPrice >= 0.1) {
        price = Math.floor(
          ratioOfLiquidities * outputLimitPrice * 100,
        ).toString();
        scale = 100;
      } else {
        scale = 10 ** countLeadingZeros(outputLimitPrice);
        price =
          firstNonZeroValue(outputLimitPrice) || outputLimitPrice.toString();
      }
      // console.log({ price, scale });

      const nonce = await routerContract.callStatic.getNonce(
        from.address,
        to.address,
        price,
        scale.toString(),
      );
      const condition = `UniswapV2Router02-${from.address.toLowerCase()}/${to.address.toLowerCase()}/${price}/${scale}/${nonce.toNumber()}`;
      // console.log({ price, scale, nonce, condition });
      // const amountOut = amountIn.mul(ratioOfLiquidities);
      const amountOut = ethers.utils.parseUnits('0', tokenDecimals);
      const payload = [amountIn, amountOut, tokens, accountAddress, deadline];

      const types = ['uint', 'uint', 'address[]', 'address', 'uint'];
      let encoded = ethers.utils.defaultAbiCoder.encode(types, payload);
      encoded = encoded.slice(2); // remove "0x"

      const uint8array = stringToUint8Array(encoded);
      const cipher = await timelockEncrypt(
        condition,
        FAIRYRING_PUBKEY,
        uint8array,
      );

      const typeOfSwap: TypeOfSwap = TypeOfSwap.swapExactTokensForTokens;

      const etherToSend = ethers.utils.parseEther('0.0001');

      if (PUBLIC_ENVIRONMENT.REACT_APP_DEBUG === 'true') {
        // debug mode only
        console.log(`0x${cipher}`, { condition, typeOfSwap, etherToSend });
      }

      const swapTx = await routerContract.encryptedSwapQueue(
        `0x${cipher}`,
        condition,
        typeOfSwap,
        {
          value: etherToSend,
        },
      );
      await swapTx.wait();
      return;
    }

    await routerContract.swapExactTokensForTokens(
      amountIn,
      amountOut,
      tokens,
      accountAddress,
      deadline,
    );
  }
};

export async function getAmountOut(
  from: TokenType,
  to: TokenType,
  amountIn: string,
  routerContract: Contract,
) {
  try {
    const valuesOut = await routerContract.getAmountsOut(
      ethers.utils.parseUnits(amountIn, from.decimals),
      [from.address, to.address],
    );
    const amountOut = ethers.utils.formatUnits(valuesOut[1]);
    console.log('amount out: ', amountOut); // eslint-disable-line no-console
    return amountOut;
  } catch {
    return false;
  }
}

export async function getAmountIn(
  from: TokenType,
  to: TokenType,
  amountOut: string,
  routerContract: Contract,
) {
  try {
    const valuesIn = await routerContract.getAmountsIn(
      ethers.utils.parseUnits(amountOut, to.decimals),
      [from.address, to.address],
    );
    const amountIn = ethers.utils.formatUnits(valuesIn[0], from.decimals);
    console.log('amountIn: ', amountIn); // eslint-disable-line no-console
    return amountIn;
  } catch {
    return false;
  }
}
